/**
 * Visual map for the engine RFQ status enum.
 *
 * Source of truth (semanticad-engine main, read-only):
 * `RfqStatus` in `services/engine/src/semanticad/rfq/models.py`:
 * draft, estimating, waiting_customer, ready, sent, cancelled, won, lost.
 *
 * A value outside that enum is not shown. The web does not invent a label
 * for it, and it does not surface the raw string.
 */
import type { MessageKey } from "./i18n";

export const RFQ_STATUS_VALUES = [
  "draft",
  "estimating",
  "waiting_customer",
  "ready",
  "sent",
  "cancelled",
  "won",
  "lost",
] as const;

export type RfqStatusValue = (typeof RFQ_STATUS_VALUES)[number];

/** Dot colour. Lost is the only state whose label is also coloured. */
export type RfqStatusTone = "neutral" | "primary" | "warning" | "success" | "danger";

export interface RfqStatusVisual {
  status: RfqStatusValue;
  tone: RfqStatusTone;
  labelKey: MessageKey;
}

const VISUAL: Record<RfqStatusValue, RfqStatusVisual> = {
  draft: { status: "draft", tone: "neutral", labelKey: "rfq.status.draft" },
  estimating: { status: "estimating", tone: "primary", labelKey: "rfq.status.estimating" },
  waiting_customer: { status: "waiting_customer", tone: "warning", labelKey: "rfq.status.waiting" },
  ready: { status: "ready", tone: "primary", labelKey: "rfq.status.ready" },
  sent: { status: "sent", tone: "primary", labelKey: "rfq.status.sent" },
  cancelled: { status: "cancelled", tone: "neutral", labelKey: "rfq.status.cancelled" },
  won: { status: "won", tone: "success", labelKey: "rfq.status.won" },
  lost: { status: "lost", tone: "danger", labelKey: "rfq.status.lost" },
};

export function rfqStatusVisual(status: string | null | undefined): RfqStatusVisual | null {
  if (!status) return null;
  const key = status.trim().toLowerCase();
  if ((RFQ_STATUS_VALUES as readonly string[]).includes(key)) {
    return VISUAL[key as RfqStatusValue];
  }
  return null;
}
